// Third party
import get from 'lodash/get';

// Types
import AppRegion from '@/types/app-region';
import BenchmarkFiltersData from '@/types/benchmark-filters-data';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import FilterType from '@/types/filter-type';
import FundFiltersData from '@/types/fund-filters-data';
import Holding from '@/types/holding';
import RiskMetric from '@/types/risk-metric';
import User from '@/types/user';

// Utils
import { formatMessage, formatLocalizedString } from './i18n';
import { userHasPermission, PERMISSIONS } from './permissions';
import { validateUserAppRegion } from './users';

export const getHoldingPerformanceVal = (holding: Holding, path: string) =>
  get(holding, `performance.${path}["3Y"]`) || get(holding, `performance.${path}["1Y"]`);

export const getRiskMetricVal = (riskMetric: RiskMetric) => get(riskMetric, '["3Y"]') || get(riskMetric, '["1Y"]');

const formatRange = (a: any, b: any) => `${a} ${formatMessage('Common.To')} ${b}`;

export const populateFundFilters = (filtersData: FundFiltersData, user?: User): FilterType[] => {
  const formatShelvesFilter = (prefix: 'eq' | 'not') => filtersData.shelves.map(shelf => `${prefix}:${shelf.id}`).join(',');
  const filters = [
    {
      key: 'universes',
      label: formatMessage('AdvancedSearch.Filters.FundType'),
      multiselect: true,
      options: filtersData.universes
        .filter(v => v)
        .map(name => ({
          value: name,
          label: name,
        })),
    },
    {
      key: 'riskRatings',
      label: formatMessage('AdvancedSearch.Filters.RiskRating'),
      multiselect: true,
      options: [
        { value: formatMessage('RiskRating.HIGH'), label: formatMessage('RiskRating.HIGH') },
        { value: formatMessage('RiskRating.MEDIUM_TO_HIGH'), label: formatMessage('RiskRating.MEDIUM_TO_HIGH') },
        { value: formatMessage('RiskRating.MEDIUM'), label: formatMessage('RiskRating.MEDIUM') },
        { value: formatMessage('RiskRating.LOW_TO_MEDIUM'), label: formatMessage('RiskRating.LOW_TO_MEDIUM') },
        { value: formatMessage('RiskRating.LOW'), label: formatMessage('RiskRating.LOW') },
        { value: formatMessage('RiskRating.VERY_LOW'), label: formatMessage('RiskRating.VERY_LOW') },
      ],
      hidden: validateUserAppRegion(user, AppRegion.US),
    },
    {
      key: 'classifications',
      label: formatMessage('AdvancedSearch.Filters.Classifications'),
      search: true,
      multiselect: true,
      options: filtersData.classifications.filter(v => v).map(name => ({ value: name, label: name })),
    },
    {
      key: 'manufacturers',
      label: formatMessage('AdvancedSearch.Filters.Manufacturers'),
      search: true,
      multiselect: true,
      options: filtersData.manufacturers.filter(v => v).map(name => ({ value: name, label: name })),
    },
    {
      key: 'MER',
      label: formatMessage('AdvancedSearch.Filters.MER'),
      type: <const>'range',
      options: [
        { label: '< 1%', value: [null, 1] },
        { label: formatRange('1', '2%'), value: [1, 2] },
        { label: formatRange('2', '3%'), value: [2, 3] },
        { label: formatRange('3', '4%'), value: [3, 4] },
        { label: formatRange('4', '5%'), value: [4, 5] },
        { label: formatRange('5', '6%'), value: [5, 6] },
        { label: '> 6%', value: [6, null] },
      ],
    },
    {
      key: 'Beta',
      label: formatMessage('AdvancedSearch.Filters.Beta'),
      type: <const>'range',
      tooltip: formatMessage('AdvancedSearch.Filters.Tooltip.1Y3Y'),
      options: [
        { label: '< 0.5', value: [null, 0.5] },
        { label: formatRange('0.5', '1'), value: [0.5, 1] },
        { label: formatRange('1', '1.5'), value: [1, 1.5] },
        { label: formatRange('1.5', '2'), value: [1.5, 2] },
        { label: formatRange('2', '3'), value: [2, 3] },
        { label: formatRange('3', '4'), value: [3, 4] },
        { label: '> 4', value: [4, null] },
      ],
    },
    {
      key: 'StDev',
      label: formatMessage('AdvancedSearch.Filters.StandardDeviation'),
      type: <const>'range',
      tooltip: formatMessage('AdvancedSearch.Filters.Tooltip.1Y3Y'),
      options: [
        { label: '< 3', value: [null, 3] },
        { label: formatRange(3, 6), value: [3, 6] },
        { label: formatRange(6, 9), value: [6, 9] },
        { label: formatRange(9, 12), value: [9, 12] },
        { label: '> 12', value: [12, null] },
      ],
    },
    {
      key: 'TNA',
      label: formatMessage('AdvancedSearch.Filters.TNA'),
      type: <const>'range',
      options: [
        { label: '< 100M', value: [null, 100000000] },
        { label: formatRange('100M', '500M'), value: [100000000, 500000000] },
        { label: formatRange('500M', '2B'), value: [500000000, 2000000000] },
        { label: formatRange('2B', '10B'), value: [2000000000, 10000000000] },
        { label: '> 10B', value: [10000000000, null] },
      ],
    },
    {
      key: 'esgRating',
      label: formatMessage('Comparison.Table.ESGOverview.ESGRating'),
      multiselect: true,
      hidden: !userHasPermission(user, PERMISSIONS.ESG),
      options: [
        { value: formatMessage('ESGRating.AAA'), label: formatMessage('ESGRating.AAA') },
        { value: formatMessage('ESGRating.AA'), label: formatMessage('ESGRating.AA') },
        { value: formatMessage('ESGRating.A'), label: formatMessage('ESGRating.A') },
        { value: formatMessage('ESGRating.BBB'), label: formatMessage('ESGRating.BBB') },
        { value: formatMessage('ESGRating.BB'), label: formatMessage('ESGRating.BB') },
        { value: formatMessage('ESGRating.B'), label: formatMessage('ESGRating.B') },
        { value: formatMessage('ESGRating.CCC'), label: formatMessage('ESGRating.CCC') },
      ],
    },
  ];

  if (filtersData.shelves && filtersData.shelves.length > 0) {
    // Display named shelves only if all the shelves have a name (shelfName under groups.shelves.shelfName)
    const shouldDisplayNamedShelves = filtersData.shelves.length > 1 && filtersData.shelves.every(shelf => shelf.shelfName);

    filters.unshift({
      key: 'shelves',
      label: formatMessage('AdvancedSearch.Filters.Shelf.Label'),
      multiselect: shouldDisplayNamedShelves,
      options: [
        ...(shouldDisplayNamedShelves
          ? filtersData.shelves.map(shelf => ({
              value: `eq:${shelf.id}`,
              label: formatLocalizedString(shelf.shelfName) || formatMessage('AdvancedSearch.Filters.Shelf.DefaultName'),
            }))
          : [{ value: formatShelvesFilter('eq'), label: formatMessage('AdvancedSearch.Filters.Shelf.OnShelf') }]),
        { value: formatShelvesFilter('not'), label: formatMessage('AdvancedSearch.Filters.Shelf.OffShelf') },
      ],
    });
  }

  return filters.filter(item => !item.hidden);
};

export const populateBenchmarkFilters = (filtersData: BenchmarkFiltersData): FilterType[] => [
  {
    key: 'classifications',
    label: formatMessage('AdvancedSearch.Filters.Classifications'),
    search: true,
    multiselect: true,
    options: filtersData.classifications.filter(v => v).map(name => ({ value: name, label: name })),
  },
  {
    key: 'currencies',
    label: formatMessage('AdvancedSearch.Filters.Currencies'),
    search: true,
    multiselect: true,
    options: filtersData.currencies.filter(v => v).map(name => ({ value: name, label: name })),
  },
  {
    key: 'StDev',
    label: formatMessage('AdvancedSearch.Filters.StandardDeviation'),
    type: 'range',
    tooltip: formatMessage('AdvancedSearch.Filters.Tooltip.1Y3Y'),
    options: [
      { label: '< 3', value: [null, 3] },
      { label: formatRange(3, 6), value: [3, 6] },
      { label: formatRange(6, 9), value: [6, 9] },
      { label: formatRange(9, 12), value: [9, 12] },
      { label: '> 12', value: [12, null] },
    ],
  },
];

export const formatBenchmark = (benchmark: ComparisonBenchmark) => ({
  ...benchmark,
  names: {
    full: benchmark.name,
    short: benchmark.name,
  },
});
